/**
 * useDrivePicker — opens the real Google Drive Picker so the user chooses the
 * destination folder themselves. Config (live OAuth token, appId, developerKey)
 * comes from the backend; nothing Google-related is hardcoded in the client.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GAPI_SRC = 'https://apis.google.com/js/api.js';

const loadGapi = () => new Promise((resolve, reject) => {
  if (window.google?.picker) { resolve(); return; }
  const done = () => window.gapi.load('picker', { callback: resolve, onerror: reject });
  const existing = document.querySelector(`script[src="${GAPI_SRC}"]`);
  if (existing) {
    if (window.gapi) done();
    else existing.addEventListener('load', done, { once: true });
    return;
  }
  const s = document.createElement('script');
  s.src = GAPI_SRC;
  s.async = true;
  s.defer = true;
  s.onload = done;
  s.onerror = () => reject(new Error('Could not load the Google Picker script'));
  document.body.appendChild(s);
});

export const useDrivePicker = () => {
  const [opening, setOpening] = useState(false);

  /** Resolves to { id, name } for the chosen folder, or null if cancelled. */
  const pickFolder = useCallback(async () => {
    setOpening(true);
    try {
      const { data } = await axios.get(`${API}/drive/picker-config`);
      if (!data.developer_key) {
        throw new Error('Google Picker API key is not configured on the server');
      }
      await loadGapi();

      return await new Promise((resolve) => {
        const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(true)
          .setMimeTypes('application/vnd.google-apps.folder');

        const picker = new window.google.picker.PickerBuilder()
          .setTitle('Choose the folder for this client')
          .setAppId(data.app_id)
          .setDeveloperKey(data.developer_key)
          .setOAuthToken(data.access_token)
          .addView(view)
          .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
          .setCallback((res) => {
            const action = res[window.google.picker.Response.ACTION];
            if (action === window.google.picker.Action.PICKED) {
              const doc = res[window.google.picker.Response.DOCUMENTS][0];
              resolve({ id: doc[window.google.picker.Document.ID], name: doc[window.google.picker.Document.NAME] });
            } else if (action === window.google.picker.Action.CANCEL) {
              resolve(null);
            }
          })
          .build();
        picker.setVisible(true);
      });
    } finally {
      setOpening(false);
    }
  }, []);

  return { pickFolder, opening };
};

export default useDrivePicker;
